// Banded symmetric positive definite systems: the normal equations of the B-spline fits (a control
// point only meets its neighbours, so A[i][j] = 0 when |i - j| > bw).

/** Symmetric band matrix, lower band stored row by row: a[i * (bw + 1) + (i - j)] for i - bw <= j <= i. */
export class Band {
  n: number;
  bw: number;
  a: Float64Array;
  constructor(n: number, bw: number) {
    this.n = n;
    this.bw = Math.min(bw, Math.max(0, n - 1));
    this.a = new Float64Array(n * (this.bw + 1));
  }
  /** A[i][j] += v (and A[j][i]). */
  add(i: number, j: number, v: number) {
    if (i < j) { const t = i; i = j; j = t; }
    if (i - j > this.bw) throw new Error(`band: (${i}, ${j}) outside bandwidth ${this.bw}`);
    this.a[i * (this.bw + 1) + i - j] += v;
  }
  get(i: number, j: number) {
    if (i < j) { const t = i; i = j; j = t; }
    return i - j > this.bw ? 0 : this.a[i * (this.bw + 1) + i - j];
  }
  diag(i: number) { return this.a[i * (this.bw + 1)]; }
  trace() { let s = 0; for (let i = 0; i < this.n; i++) s += this.diag(i); return s; }
  /** this += s * B (same shape). */
  addScaled(B: Band, s: number) {
    if (B.n !== this.n || B.bw > this.bw) throw new Error('band: shapes differ');
    for (let i = 0; i < this.n; i++) for (let d = 0; d <= B.bw && d <= i; d++) this.a[i * (this.bw + 1) + d] += s * B.a[i * (B.bw + 1) + d];
  }
  clone() { const b = new Band(this.n, this.bw); b.a.set(this.a); return b; }
}

/** In-place Cholesky A = L Lᵀ (L in the band). false if A is not positive definite. */
export function cholBand(B: Band): boolean {
  const { n, bw, a } = B, w = bw + 1;
  for (let i = 0; i < n; i++) {
    const j0 = Math.max(0, i - bw);
    for (let j = j0; j <= i; j++) {
      let s = a[i * w + i - j];
      for (let k = Math.max(j0, j - bw); k < j; k++) s -= a[i * w + i - k] * a[j * w + j - k];
      if (i === j) {
        if (!(s > 0)) return false;
        a[i * w] = Math.sqrt(s);
      } else a[i * w + i - j] = s / a[j * w];
    }
  }
  return true;
}

/** Solve L Lᵀ x = b in place (B holds the factor from cholBand). */
export function solveBand(B: Band, b: Float64Array) {
  const { n, bw, a } = B, w = bw + 1;
  for (let i = 0; i < n; i++) {
    let s = b[i];
    for (let k = Math.max(0, i - bw); k < i; k++) s -= a[i * w + i - k] * b[k];
    b[i] = s / a[i * w];
  }
  for (let i = n - 1; i >= 0; i--) {
    let s = b[i];
    for (let k = i + 1; k <= Math.min(n - 1, i + bw); k++) s -= a[k * w + k - i] * b[k];
    b[i] = s / a[i * w];
  }
  return b;
}

/**
 * x with A x = b for a symmetric positive (semi-)definite band A (A is left as it was). A semi-definite
 * A (a control point no data or penalty reaches) gets a small ridge, growing until it factors.
 */
export function solveSPD(A: Band, b: Float64Array): Float64Array {
  let max = 0;
  for (let i = 0; i < A.n; i++) max = Math.max(max, Math.abs(A.diag(i)));
  for (let ridge = 0, tries = 0; tries < 8; tries++, ridge = ridge ? ridge * 100 : 1e-12 * (max || 1)) {
    const L = A.clone();
    for (let i = 0; i < A.n; i++) L.a[i * (L.bw + 1)] += ridge;
    if (cholBand(L)) return solveBand(L, Float64Array.from(b));
  }
  throw new Error('band: system is not positive definite');
}

/** A x. */
export function mulBand(A: Band, x: ArrayLike<number>, out = new Float64Array(A.n)) {
  const { n, bw, a } = A, w = bw + 1;
  out.fill(0);
  for (let i = 0; i < n; i++) {
    out[i] += a[i * w] * x[i];
    for (let d = 1; d <= bw && d <= i; d++) { const v = a[i * w + d]; out[i] += v * x[i - d]; out[i - d] += v * x[i]; }
  }
  return out;
}
